import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaGithub, FaLinkedin, FaHome, FaUser, FaCode, FaEnvelope, FaBars, FaTimes } from "react-icons/fa";
import ScrollToTop from './ScrollToTop';

const Layout = ({ children }) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path ? "text-cyan-400 border-b-2 border-cyan-400" : "text-gray-300 hover:text-cyan-400 transition-colors";

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-white font-sans selection:bg-cyan-500 selection:text-white">
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-cyan-500/30 sticky top-0 z-50 shadow-[0_0_15px_rgba(6,182,212,0.1)]">
        <nav className="container mx-auto flex justify-between items-center p-4">
          {/* Logo Area */}
          <Link to="/" className="w-24 h-12 group relative z-50">
            <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <svg viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg" className="w-full h-full relative z-10">
              <rect width="300" height="280" rx="15" fill="#0f172a" stroke="#06b6d4" strokeWidth="2"/>

              <g stroke="#06b6d4" strokeWidth="0.5" opacity="0.3">
                <line x1="20" y1="0" x2="20" y2="100"/>
                <line x1="60" y1="0" x2="60" y2="100"/>
                <line x1="100" y1="0" x2="100" y2="100"/>
                <line x1="140" y1="0" x2="140" y2="100"/>
                <line x1="180" y1="0" x2="180" y2="100"/>
                <line x1="0" y1="25" x2="200" y2="25"/>
                <line x1="0" y1="50" x2="200" y2="50"/>
                <line x1="0" y1="75" x2="200" y2="75"/>
              </g>

              <text x="50%" y="75%" textAnchor="middle" fontFamily="monospace" fontWeight="bold" fontSize="80" fill="#06b6d4" letterSpacing="4">
                SM
              </text>
            </svg>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-8 font-mono text-base">
            <Link to="/" className={`flex items-center gap-2 ${isActive('/')}`}>
              <FaHome /> <span className="tracking-widest">HOME</span>
            </Link>
            <Link to="/about" className={`flex items-center gap-2 ${isActive('/about')}`}>
              <FaUser /> <span className="tracking-widest">ABOUT</span>
            </Link>
            <Link to="/projects" className={`flex items-center gap-2 ${isActive('/projects')}`}>
              <FaCode /> <span className="tracking-widest">PROJECTS</span>
            </Link>
            <Link to="/contact" className={`flex items-center gap-2 ${isActive('/contact')}`}>
              <FaEnvelope /> <span className="tracking-widest">CONTACT</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-cyan-400 z-50 focus:outline-none active:scale-95 transition-transform"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <FaTimes size={28} /> : <FaBars size={28} />}
          </button>

          {/* Mobile Navigation Overlay */}
          <div className={`
            absolute top-full left-0 w-full 
            bg-slate-900/95 backdrop-blur-xl border-b border-cyan-500/30 
            shadow-lg z-40 
            flex flex-row justify-around items-center py-4 
            transition-all duration-300 ease-in-out 
            md:hidden
            ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}
          `}>
            <Link to="/" onClick={toggleMenu} className={`text-xs font-mono flex flex-col items-center gap-1 ${isActive('/')}`}>
              <FaHome size={18} /> <span>HOME</span>
            </Link>
            <Link to="/about" onClick={toggleMenu} className={`text-xs font-mono flex flex-col items-center gap-1 ${isActive('/about')}`}>
              <FaUser size={18} /> <span>ABOUT</span>
            </Link>
            <Link to="/projects" onClick={toggleMenu} className={`text-xs font-mono flex flex-col items-center gap-1 ${isActive('/projects')}`}>
              <FaCode size={18} /> <span>PROJECTS</span>
            </Link>
            <Link to="/contact" onClick={toggleMenu} className={`text-xs font-mono flex flex-col items-center gap-1 ${isActive('/contact')}`}>
              <FaEnvelope size={18} /> <span>CONTACT</span>
            </Link>
          </div>
        </nav>
      </header>

      <main className="flex-grow container mx-auto p-6 relative">
        {/* Background Grid Effect */}
        <div className="fixed inset-0 pointer-events-none z-[-1] opacity-10" 
             style={{backgroundImage: 'linear-gradient(#06b6d4 1px, transparent 1px), linear-gradient(90deg, #06b6d4 1px, transparent 1px)', backgroundSize: '40px 40px'}}>
        </div>
        {children}
      </main>

      <ScrollToTop />

      <footer className="bg-slate-900 border-t border-cyan-500/30 text-gray-400 py-6 mt-auto">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between px-4 gap-4">
          
          <div className="flex space-x-6">
            <a
              href="https://github.com/sphesihlemhlongo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-cyan-400 transition-all transform hover:scale-110 active:scale-95"
            >
              <FaGithub size={24} />
            </a>
            <a
              href="https://www.linkedin.com/in/sphesihle-mhlongo-937858267/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-cyan-400 transition-all transform hover:scale-110 active:scale-95"
            >
              <FaLinkedin size={24} />
            </a>
          </div>

          <div className="font-mono text-xs md:text-sm text-center">
            <p>&copy; {currentYear} Sphesihle Mhlongo <span className="text-cyan-500">|</span> AI ENGINEER</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
